/**
 * When two teams share (nearly) the same kit primary, side-by-side colour cues — H2H win
 * tallies, the results bar, etc. — become indistinguishable. This nudges the away colour
 * (hue rotation + a lightness shift) only when the two are too close, leaving distinct kits
 * untouched.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function normalizeHex(hex: string): string | null {
  const h = hex.trim();
  if (!h.startsWith('#')) return null;
  const body = h.slice(1);
  if (body.length === 3 && /^[0-9A-Fa-f]{3}$/.test(body)) {
    return `#${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`.toUpperCase();
  }
  if (body.length === 6 && /^[0-9A-Fa-f]{6}$/.test(body)) {
    return `#${body.toUpperCase()}`;
  }
  return null;
}

function hexToRgb(hex: string): Rgb | null {
  const n = normalizeHex(hex);
  if (n === null) return null;
  return {
    r: parseInt(n.slice(1, 3), 16),
    g: parseInt(n.slice(3, 5), 16),
    b: parseInt(n.slice(5, 7), 16),
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (c: number): string =>
    Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: h * 60, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = l - c / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/** Straight RGB Euclidean distance (0–441). Below ~70 reads as "basically the same colour". */
function colorDistance(a: Rgb, b: Rgb): number {
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

const SIMILAR_THRESHOLD = 70;

/**
 * Returns `{ home, away }` hex colours guaranteed to be visually distinct. `home` is always
 * returned unchanged; `away` is nudged only when it's too close to `home`.
 */
export function distinguishTeamColors(
  homeHex: string,
  awayHex: string,
): { home: string; away: string } {
  const homeRgb = hexToRgb(homeHex);
  const awayRgb = hexToRgb(awayHex);
  if (homeRgb === null || awayRgb === null) return { home: homeHex, away: awayHex };
  if (colorDistance(homeRgb, awayRgb) >= SIMILAR_THRESHOLD) {
    return { home: homeHex, away: awayHex };
  }

  const { h, s, l } = rgbToHsl(awayRgb);
  // Rotate hue for saturated colours; shift lightness so greys (and near-greys) still separate.
  const rotatedHue = h + 50;
  const shiftedL = l > 0.5 ? Math.max(0.18, l - 0.26) : Math.min(0.82, l + 0.26);
  const boostedS = Math.min(1, s + 0.15);
  const nudged = rgbToHex(hslToRgb(rotatedHue, boostedS, shiftedL));
  return { home: homeHex, away: nudged };
}
